/**
 * Hook for detecting stuck slide generations
 *
 * Monitors generating slides and flags them as potentially stuck
 * if they've been generating for longer than expected thresholds.
 */

import { useMemo, useState, useEffect, useCallback } from 'react';
import type { SlideOutline } from '@/types/outline';

// Thresholds for stuck detection (in milliseconds)
export const STUCK_THRESHOLDS = {
  WARNING: 2 * 60 * 1000,      // 2 minutes - show amber warning
  STUCK: 5 * 60 * 1000,        // 5 minutes - flag as stuck
  CRITICAL: 10 * 60 * 1000,    // 10 minutes - definitely stuck
};

export interface StuckSlideInfo {
  slideId: string;
  elapsedMs: number;
  elapsedFormatted: string;
  severity: 'normal' | 'warning' | 'stuck' | 'critical';
  generationStartedAt: string;
}

export interface StuckSlideDetectionResult {
  /** Map of slideId -> stuck info for all generating slides */
  stuckInfo: Record<string, StuckSlideInfo>;
  /** Whether any slides are potentially stuck (> WARNING threshold) */
  hasWarningSlides: boolean;
  /** Whether any slides are definitely stuck (> STUCK threshold) */
  hasStuckSlides: boolean;
  /** List of slide IDs that are stuck */
  stuckSlideIds: string[];
  /** Force a refresh of elapsed times */
  refresh: () => void;
}

/**
 * Format elapsed time in a human-readable format
 */
function formatElapsedTime(ms: number): string {
  if (ms < 1000) return '0s';

  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes === 0) {
    return `${seconds}s`;
  }

  if (minutes < 60) {
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0
    ? `${hours}h ${remainingMinutes}m`
    : `${hours}h`;
}

/**
 * Determine severity based on elapsed time
 */
function getSeverity(elapsedMs: number): StuckSlideInfo['severity'] {
  if (elapsedMs >= STUCK_THRESHOLDS.CRITICAL) return 'critical';
  if (elapsedMs >= STUCK_THRESHOLDS.STUCK) return 'stuck';
  if (elapsedMs >= STUCK_THRESHOLDS.WARNING) return 'warning';
  return 'normal';
}

/**
 * Hook to detect stuck slide generations and track elapsed time
 *
 * @param slides - Array of slides to monitor
 * @param refreshInterval - How often to update elapsed times (default: 1000ms)
 */
export function useStuckSlideDetection(
  slides: SlideOutline[] | undefined,
  refreshInterval: number = 1000
): StuckSlideDetectionResult {
  const [tick, setTick] = useState(0);

  // Find generating slides
  const generatingSlides = useMemo(() => {
    if (!slides) return [];
    return slides.filter(
      s => s.generationStatus === 'generating' && !s.isDeleted
    );
  }, [slides]);

  // Set up interval to update elapsed times
  useEffect(() => {
    if (generatingSlides.length === 0) return;

    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [generatingSlides.length, refreshInterval]);

  // Manual refresh function
  const refresh = useCallback(() => {
    setTick(t => t + 1);
  }, []);

  // Calculate stuck info for all generating slides
  const result = useMemo((): StuckSlideDetectionResult => {
    const now = Date.now();
    const stuckInfo: Record<string, StuckSlideInfo> = {};
    let hasWarningSlides = false;
    let hasStuckSlides = false;
    const stuckSlideIds: string[] = [];

    for (const slide of generatingSlides) {
      // Calculate elapsed time
      let elapsedMs = 0;

      if (slide.generationStartedAt) {
        const startedAt = new Date(slide.generationStartedAt).getTime();
        elapsedMs = now - startedAt;
      } else if (slide.isStuck) {
        // If backend flagged as stuck but no start time, assume > STUCK threshold
        elapsedMs = STUCK_THRESHOLDS.STUCK + 1;
      }

      const severity = getSeverity(elapsedMs);

      stuckInfo[slide.id] = {
        slideId: slide.id,
        elapsedMs,
        elapsedFormatted: formatElapsedTime(elapsedMs),
        severity,
        generationStartedAt: slide.generationStartedAt || '',
      };

      if (severity === 'warning' || severity === 'stuck' || severity === 'critical') {
        hasWarningSlides = true;
      }

      if (severity === 'stuck' || severity === 'critical') {
        hasStuckSlides = true;
        stuckSlideIds.push(slide.id);
      }

      // Also check backend isStuck flag
      if (slide.isStuck) {
        hasStuckSlides = true;
        if (!stuckSlideIds.includes(slide.id)) {
          stuckSlideIds.push(slide.id);
        }
      }
    }

    return {
      stuckInfo,
      hasWarningSlides,
      hasStuckSlides,
      stuckSlideIds,
      refresh,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatingSlides, tick, refresh]);

  return result;
}

/**
 * Get stuck info for a single slide
 */
export function getSlideStuckInfo(slide: SlideOutline | undefined): StuckSlideInfo | null {
  if (!slide || slide.generationStatus !== 'generating') {
    return null;
  }

  const now = Date.now();
  let elapsedMs = 0;

  if (slide.generationStartedAt) {
    const startedAt = new Date(slide.generationStartedAt).getTime();
    elapsedMs = now - startedAt;
  } else if (slide.isStuck) {
    elapsedMs = STUCK_THRESHOLDS.STUCK + 1;
  }

  return {
    slideId: slide.id,
    elapsedMs,
    elapsedFormatted: formatElapsedTime(elapsedMs),
    severity: getSeverity(elapsedMs),
    generationStartedAt: slide.generationStartedAt || '',
  };
}

export default useStuckSlideDetection;
